import { computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import type { 
  RegisterData,
  LoginData,
  ApiError,
  ForgotPasswordData,
  ResetPasswordData
} from '@/types';
import { getApiErrorMessage } from '@/utils/apiError';
import { i18n } from '@/plugins/i18n';
import { redirectToAuthService } from '@/services/AuthServiceNavigation';

export const useAuth = () => {
  const authStore = useAuthStore();
  const router = useRouter();
  const route = useRoute();

  const getRedirectPath = (): string => {
    const redirect = route.query.redirect;
    if (redirect && typeof redirect === 'string') {
      return decodeURIComponent(redirect);
    }
    return '/galleries';
  };

  const register = async (_data: RegisterData) => {
    try {
      redirectToAuthService('/register');
      return { success: true, message: null, errors: null };
    } catch (error: unknown) {
      const apiError = error as ApiError;
      return {
        success: false,
        message: apiError.data?.message || getApiErrorMessage(error, i18n.global.t('auth.messages.registrationFailed')),
        errors: apiError.data?.errors,
      };
    }
  };

  const login = async (_credentials: LoginData) => {
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('auth_redirect_after_login', getRedirectPath());
      }

      await authStore.initiateOAuthLogin();
      return { success: true, message: null, errors: null };
    } catch (error: unknown) {
      const apiError = error as ApiError;
      return {
        success: false,
        message: apiError.data?.message || getApiErrorMessage(error, i18n.global.t('auth.messages.loginFailed')),
        errors: apiError.data?.errors,
      };
    }
  };

  const forgotPassword = async (_data: ForgotPasswordData) => {
    try {
      redirectToAuthService('/forgot-password');
      return { success: true, message: null, errors: null };
    } catch (error: unknown) {
      const apiError = error as ApiError;
      return {
        success: false,
        message: apiError.data?.message || getApiErrorMessage(error, i18n.global.t('auth.messages.passwordResetRequestFailed')),
        errors: apiError.data?.errors,
      };
    }
  };

  const resetPassword = async (data: ResetPasswordData) => {
    try {
      redirectToAuthService(`/reset-password/${encodeURIComponent(data.token)}`, {
        email: data.email,
      });
      return { success: true, message: null, errors: null };
    } catch (error: unknown) {
      const apiError = error as ApiError;
      return {
        success: false,
        message: apiError.data?.message || getApiErrorMessage(error, i18n.global.t('auth.messages.passwordResetFailed')),
        errors: apiError.data?.errors,
      };
    }
  };

  const logout = async () => {
    await authStore.logout();
    await router.push('/');
  };

  return {
    user: computed(() => authStore.user),
    isAuthenticated: computed(() => authStore.isAuthenticated),
    loading: computed(() => authStore.loading),
    register,
    login,
    logout,
    forgotPassword,
    resetPassword
  };
};
